import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

import { Skeleton } from '@shared/components/Skeleton';

const FiltersSkeleton = () => (
  <Flex
    gap={6}
    padding={6}
    width={pxToRem(280)}
    flexDirection="column"
  >
    <Skeleton
      width="60%"
      height={pxToRem(24)}
    />
    {[0, 1, 2, 3].map(section => (
      <Flex
        gap={4}
        key={section}
        flexDirection="column"
      >
        <Skeleton
          width="50%"
          height={pxToRem(18)}
        />
        {[0, 1, 2].map(row => (
          <Flex
            gap={3}
            key={row}
            alignItems="center"
          >
            <Skeleton
              borderRadius={2}
              width={pxToRem(20)}
              height={pxToRem(20)}
            />
            <Skeleton
              width="70%"
              height={pxToRem(16)}
            />
          </Flex>
        ))}
      </Flex>
    ))}
  </Flex>
);

const DetailRowSkeleton = () => (
  <Flex
    gap={3}
    alignItems="center"
  >
    <Skeleton
      flexShrink={0}
      width={pxToRem(18)}
      height={pxToRem(18)}
      borderRadius="50%"
    />
    <Skeleton
      width={pxToRem(120)}
      height={pxToRem(16)}
    />
  </Flex>
);

const LocationDetailSkeleton = () => (
  <Flex
    flex={1}
    gap={3}
    minWidth={0}
    alignItems="flex-start"
  >
    <Skeleton
      flexShrink={0}
      width={pxToRem(18)}
      height={pxToRem(18)}
      borderRadius="50%"
    />
    <Flex
      gap={1}
      minWidth={0}
      flex={1}
      flexDirection="column"
    >
      <Skeleton
        width="75%"
        height={pxToRem(20)}
      />
      <Skeleton
        width="55%"
        height={pxToRem(14)}
      />
      <Skeleton
        width="90%"
        height={pxToRem(14)}
      />
    </Flex>
  </Flex>
);

const CardSkeleton = () => (
  <Flex
    borderRadius={5}
    overflow="hidden"
    border="solid 1px"
    borderColor="mono3"
    minHeight={{ sm2: pxToRem(230) }}
    flexDirection={{ _: 'column', sm2: 'row' }}
  >
    <Box
      padding={4}
      flexShrink={0}
      backgroundColor="white"
      alignSelf={{ sm2: 'stretch' }}
      minWidth={{ sm2: pxToRem(250) }}
      maxWidth={{ sm2: pxToRem(420) }}
      width={{ _: '100%', sm2: '16%' }}
      borderRightWidth={{ _: 0, sm2: '1px' }}
      borderBottomWidth={{ _: '1px', sm2: 0 }}
      height={{ _: pxToRem(220), sm2: 'auto' }}
      borderRightStyle={{ _: 'none', sm2: 'solid' }}
      borderBottomStyle={{ _: 'solid', sm2: 'none' }}
      borderRightColor={{ _: 'transparent', sm2: 'mono3' }}
      borderBottomColor={{ _: 'mono3', sm2: 'transparent' }}
    >
      <Skeleton
        width="100%"
        height="100%"
        minHeight={pxToRem(180)}
      />
    </Box>

    <Flex
      gap={6}
      flex={1}
      padding={6}
      minWidth={0}
      alignItems="stretch"
      flexDirection={{ _: 'column', sm2: 'row' }}
    >
      <Flex
        gap={4}
        flex={1}
        minWidth={0}
        flexDirection="column"
      >
        <Flex
          gap={6}
          width="100%"
          alignItems="flex-start"
          flexDirection={{ _: 'column', sm2: 'row' }}
        >
          <Flex
            gap={4}
            minWidth={0}
            flex={{ sm2: 1 }}
            flexDirection="column"
          >
            <Box minWidth={0}>
              <Skeleton
                width="55%"
                height={pxToRem(24)}
              />
              <Skeleton
                marginTop={2}
                width="70%"
                height={pxToRem(16)}
              />
            </Box>
            <Flex gap={3}>
              <DetailRowSkeleton />
              <DetailRowSkeleton />
            </Flex>
            <LocationDetailSkeleton />
          </Flex>

          <Flex
            gap={4}
            minWidth={0}
            flex={{ sm2: 1 }}
            flexDirection="column"
          >
            <Flex
              gap={2}
              flexDirection="column"
            >
              <Skeleton
                width="70%"
                height={pxToRem(14)}
              />
              <Skeleton
                width="55%"
                height={pxToRem(14)}
              />
              <Skeleton
                width="60%"
                height={pxToRem(14)}
              />
            </Flex>
            <Flex
              gap={3}
              alignItems="center"
            >
              <Skeleton
                width={pxToRem(72)}
                height={pxToRem(16)}
              />
              <Skeleton
                width={pxToRem(96)}
                height={pxToRem(16)}
              />
            </Flex>
            <LocationDetailSkeleton />
          </Flex>
        </Flex>
      </Flex>

      <Flex
        gap={3}
        flexShrink={0}
        alignItems="flex-start"
        justifyContent="flex-end"
        minWidth={{ sm2: pxToRem(180) }}
        width={{ _: '100%', sm2: 'auto' }}
      >
        <Flex
          gap={2}
          flexDirection="column"
          alignItems={{ _: 'flex-start', sm2: 'flex-end' }}
        >
          <Skeleton
            width={pxToRem(64)}
            height={pxToRem(24)}
            borderRadius={2}
          />
          <Skeleton
            width={pxToRem(112)}
            height={pxToRem(28)}
          />
          <Skeleton
            width={pxToRem(128)}
            height={pxToRem(14)}
          />
          <Skeleton
            width={pxToRem(88)}
            height={pxToRem(14)}
          />
          <Skeleton
            width={{ _: '100%', sm2: pxToRem(120) }}
            height={pxToRem(40)}
            borderRadius={pxToRem(999)}
          />
        </Flex>
      </Flex>
    </Flex>
  </Flex>
);

export const CarListingsSkeleton = () => (
  <Flex
    flex={1}
    minWidth={0}
    flexDirection={{ _: 'column', md: 'row' }}
  >
    <Flex
      flexShrink={0}
      borderRight="1px solid"
      borderRightColor="mono3"
      display={{ _: 'none', md: 'flex' }}
    >
      <FiltersSkeleton />
    </Flex>

    <Flex
      flex={1}
      rowGap={6}
      minWidth={0}
      flexDirection="column"
      paddingX={{ _: 4, sm2: 8 }}
      paddingY={{ _: 6, sm2: 8 }}
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
      >
        <Skeleton
          width={pxToRem(200)}
          height={pxToRem(24)}
        />
        <Skeleton
          width={pxToRem(140)}
          height={pxToRem(36)}
          borderRadius={pxToRem(999)}
        />
      </Flex>
      {[0, 1, 2, 3].map(index => (
        <CardSkeleton key={index} />
      ))}
    </Flex>
  </Flex>
);
